import { useEffect, useId, useState, type ReactNode } from 'react';

interface Props {
  label: ReactNode;
  /** Plain-text accessible name (the visible label may contain glossary buttons). */
  name: string;
  value: number;
  min: number;
  max: number;
  step: number;
  unit?: string;
  /** Called when the user finishes a change (release, Enter, blur). */
  onCommit: (v: number) => void;
  /** Called continuously while dragging (used for live manual input). */
  onLive?: (v: number) => void;
  disabled?: boolean;
  hint?: string;
}

/** Slider + exact number entry. Settings changes commit once per gesture, not per pixel. */
export function NumberField({ label, name, value, min, max, step, unit, onCommit, onLive, disabled, hint }: Props) {
  const id = useId();
  const [local, setLocal] = useState(String(value));
  const [dragging, setDragging] = useState(false);
  useEffect(() => {
    if (!dragging) setLocal(String(+value.toFixed(6)));
  }, [value, dragging]);

  const commit = (raw: string) => {
    const x = Number(raw.replace(',', '.').replace('−', '-'));
    if (!Number.isFinite(x)) {
      setLocal(String(value));
      return;
    }
    if (x !== value) onCommit(x);
  };

  const num = Number(local);
  return (
    <div className="field">
      <div className="field-label">
        {label}
        {unit && <span className="muted"> ({unit})</span>}
      </div>
      <div className="field-row">
        <input
          type="range"
          aria-label={`${name}${unit ? ` (${unit})` : ''}, slider`}
          min={min}
          max={max}
          step={step}
          value={Number.isFinite(num) ? Math.min(max, Math.max(min, num)) : value}
          disabled={disabled}
          onChange={(e) => {
            setLocal(e.target.value);
            setDragging(true);
            onLive?.(Number(e.target.value));
          }}
          onPointerUp={(e) => {
            setDragging(false);
            commit((e.target as HTMLInputElement).value);
          }}
          onKeyUp={(e) => {
            setDragging(false);
            commit((e.target as HTMLInputElement).value);
          }}
          onBlur={(e) => {
            if (dragging) {
              setDragging(false);
              commit(e.target.value);
            }
          }}
        />
        <input
          id={id}
          aria-label={`${name}${unit ? ` (${unit})` : ''}`}
          type="text"
          inputMode="decimal"
          className="num"
          value={local}
          disabled={disabled}
          onChange={(e) => setLocal(e.target.value)}
          onBlur={(e) => commit(e.target.value)}
          onKeyDown={(e) => {
            if (e.key === 'Enter') commit((e.target as HTMLInputElement).value);
          }}
        />
      </div>
      {hint && <p className="hint">{hint}</p>}
    </div>
  );
}
